/**
 * Billing API Service
 * Handles all billing-related API calls
 */
import api from './api';
import type {
  Plan,
  PlanFeature,
  Subscription,
  SubscriptionResponse,
  Entitlement,
  EntitlementsResponse,
  CheckoutSession,
  CreateCheckoutRequest,
  CouponValidation,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  PaymentHistoryResponse,
  TrialStatus,
} from '@/types/billing';

// Feature key to display label mapping
const FEATURE_LABELS: Record<string, string> = {
  basic_features: 'Basic features',
  unlimited_connections: 'Unlimited bank connections',
  statement_access: 'Statement import',
  export: 'Data export',
  advanced_analytics: 'Advanced analytics',
  priority_support: 'Priority support',
  family_sharing: 'Family sharing',
  api_access: 'API access',
};

/**
 * Transform backend feature strings to PlanFeature objects
 */
function transformFeatures(features: string[]): PlanFeature[] {
  return features.map((key) => ({
    key,
    label: FEATURE_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    included: true,
  }));
}

// ============================================================================
// Billing API
// ============================================================================

export const billingApi = {
  // ---------------------------------------------------------------------------
  // Plans
  // ---------------------------------------------------------------------------

  /**
   * Get all available plans
   * Backend now returns camelCase, but we still transform feature strings to PlanFeature objects
   */
  getPlans: async (): Promise<Plan[]> => {
    interface ApiPlan {
      id: string;
      name: string;
      amount: number;
      currency: string;
      interval: string;
      features: string[];
      isActive: boolean;
      displayOrder: number;
      trialPeriodDays: number;
    }
    interface ApiPlansResponse {
      plans: ApiPlan[];
      count: number;
    }
    const response = await api.get<ApiPlansResponse>('/billing/plans');
    return response.plans.map((plan) => ({
      id: plan.id,
      name: plan.name,
      description: '', // Backend doesn't send description
      amount: plan.amount,
      currency: plan.currency.toUpperCase(),
      interval: plan.interval as 'monthly' | 'yearly',
      features: transformFeatures(plan.features),
      trialPeriodDays: plan.trialPeriodDays,
      isPopular: plan.displayOrder === 2, // Annual plan is "popular"
      sortOrder: plan.displayOrder,
    }));
  },

  /**
   * Get a single plan by ID
   */
  getPlan: async (planId: string): Promise<Plan> => {
    return api.get<Plan>(`/billing/plans/${planId}`);
  },

  // ---------------------------------------------------------------------------
  // Subscription
  // ---------------------------------------------------------------------------

  /**
   * Get current subscription
   */
  getSubscription: async (): Promise<SubscriptionResponse> => {
    return api.get<SubscriptionResponse>('/billing/subscription');
  },

  /**
   * WP-BILLING-19 Part B: move the household's subscription to another plan, in
   * place (Stripe prorates; nothing is charged during a trial).
   */
  changePlan: async (planId: string): Promise<void> => {
    await api.post('/billing/subscription/change-plan', { planId });
  },

  /**
   * Cancel subscription
   * @param immediately - If true, cancel immediately. If false, cancel at period end.
   * @param reason - Optional cancellation reason for feedback
   */
  cancelSubscription: async (
    request: CancelSubscriptionRequest = {}
  ): Promise<CancelSubscriptionResponse> => {
    return api.post<CancelSubscriptionResponse>('/billing/subscription/cancel', request);
  },

  /**
   * Resume a canceled subscription (if cancelAtPeriodEnd is true)
   */
  resumeSubscription: async (): Promise<Subscription> => {
    return api.post<Subscription>('/billing/subscription/resume', {});
  },

  // ---------------------------------------------------------------------------
  // Checkout
  // ---------------------------------------------------------------------------

  /**
   * Create a Stripe Checkout session for subscription
   * Backend now returns camelCase directly
   */
  createCheckoutSession: async (
    request: CreateCheckoutRequest
  ): Promise<CheckoutSession> => {
    return api.post<CheckoutSession>('/billing/checkout', request);
  },

  /**
   * Sync subscription from a completed Stripe checkout session.
   *
   * WP-BUG-BILLING-SYNC: Fallback mechanism for when webhooks are delayed.
   * Called when user returns from Stripe checkout to ensure subscription
   * is immediately synced, even if webhook hasn't arrived yet.
   *
   * @param sessionId - Stripe checkout session ID (cs_xxx)
   */
  syncFromCheckout: async (sessionId: string): Promise<Subscription | null> => {
    return api.post<Subscription | null>('/billing/sync-from-checkout', {
      sessionId,
    });
  },

  // ---------------------------------------------------------------------------
  // Entitlements
  // ---------------------------------------------------------------------------

  /**
   * Get tenant's active entitlements
   */
  getEntitlements: async (): Promise<Entitlement[]> => {
    const response = await api.get<EntitlementsResponse>('/billing/entitlements');
    return response.entitlements;
  },

  /**
   * Check if a specific feature is entitled
   */
  checkEntitlement: async (featureKey: string): Promise<boolean> => {
    const entitlements = await billingApi.getEntitlements();
    return entitlements.some(
      (e) => e.featureKey === featureKey && e.isActive
    );
  },

  // ---------------------------------------------------------------------------
  // Coupons
  // ---------------------------------------------------------------------------

  /**
   * Validate a coupon code
   */
  validateCoupon: async (code: string): Promise<CouponValidation> => {
    return api.post<CouponValidation>('/billing/coupons/validate', { code });
  },

  // ---------------------------------------------------------------------------
  // Payment History
  // ---------------------------------------------------------------------------

  /**
   * Get payment history
   * @param cursor - Pagination cursor for loading more payments
   * @param limit - Number of payments to fetch (default: 10)
   */
  getPaymentHistory: async (
    cursor?: string,
    limit: number = 10
  ): Promise<PaymentHistoryResponse> => {
    const params: Record<string, unknown> = { limit };
    if (cursor) {
      params.cursor = cursor;
    }
    return api.get<PaymentHistoryResponse>('/billing/payments', params);
  },

  // ---------------------------------------------------------------------------
  // Billing Portal
  // ---------------------------------------------------------------------------

  /**
   * Get Stripe customer portal URL for managing payment methods
   */
  getPortalUrl: async (returnUrl?: string): Promise<{ url: string }> => {
    const params = returnUrl ? { returnUrl: returnUrl } : undefined;
    // Backend PortalSessionResponse.portal_url -> camelCase portalUrl
    // (services/billing/app/schemas/checkout.py). Reading `url` here got
    // undefined; WP-BILLING-20 fixed both ends.
    const response = await api.post<{ portalUrl: string }>('/billing/portal', params);
    return { url: response.portalUrl };
  },

  // ---------------------------------------------------------------------------
  // Trial Status
  // ---------------------------------------------------------------------------

  /**
   * Get trial status for current tenant
   * Endpoint is in auth service at /auth/trial-status
   */
  getTrialStatus: async (): Promise<TrialStatus> => {
    return api.get<TrialStatus>('/auth/trial-status');
  },
};

export default billingApi;
